// ─────────────────────────────────────────────
//  Channels domain — pure derivation of today's messaging channels from the
//  daily roster + the live rooms assignment graph. No Firestore, no React:
//  just data shaping so it is unit-testable (mirrors src/domain/alerts.js).
//
//  This module is the single source of truth for "who is in which channel
//  today" (D-04/D-05) and closes the A1 / Pitfall-1 trap:
//
//    Roster ids (crnas[].id, supervisors[].id, aicId) and room ids
//    (providerId, supervisingDoctorId) reference `staff` DOC IDS, but security
//    rules and delivery key on `request.auth.uid`. Membership MUST be resolved
//    through the nullable `staff.uid` link (see domain/staff.js: "null =
//    pool-only, cannot receive alerts"). A null-uid (pool-only) member CANNOT
//    log in to chat and is DROPPED from every memberIds array. Putting a raw
//    staff id into memberIds would make the rule (`uid in memberIds`) silently
//    deny the real, correctly-assigned user.
//
//  Channel docs are day-scoped by a deterministic id so tomorrow's derivation
//  produces different ids — yesterday's channels simply fall out of the set
//  (archived by omission, D-07). No cleanup job, no Cloud Function.
// ─────────────────────────────────────────────

import { occupantRowForRoom } from "./roomOccupant";
import { assignmentForRoom } from "./gridTemplate";
import { isSoloCase } from "./timeline";

// The derived (non-DM) channel kinds.
export const CHANNEL_KINDS = {
  broadcast: "broadcast",
  allCrnas: "allCrnas",   // "CRNA/AA Only" — providers (CRNA/AA), residents + doctors excluded
  allDoctors: "allDoctors",
  aic: "aic",             // doctors ↔ the AIC of the day
  aicCoordination: "aicCoordination", // cross-facility AIC-only coordination room ("All AICs")
  perOr: "perOr",         // a CRNA + their supervising doctor ("me & my supervisor")
  careTeam: "careTeam",
  custom: "custom",       // ad-hoc group room built by hand (not derived)
};

// Deterministic, day-scoped channel doc id. A different dateKey yields a
// disjoint id for the same kind+scope, which is what gives the free daily
// reset (D-07): tomorrow's ids are never the same as today's.
export function channelDocId(facilityId, dateKey, kind, scopeId) {
  return `${facilityId}_${dateKey}_${kind}_${scopeId}`;
}

// ── A1 / Pitfall-1: staff doc id → auth uid ──
// Resolve a roster/room id (a staff doc id, OR an id that is already an auth
// uid) to its auth uid via the staff list's `uid` link. Returns null when the
// staff entry's uid is null (pool-only, cannot log in) OR the id is not found.
// Tolerant of an id passed as a uid directly (matches findInPool's dual-key
// behavior during the staff↔uid migration).
export function uidOf(staff = [], id) {
  if (!id) return null;
  const entry = (staff || []).find((s) => s && (s.id === id || s.uid === id));
  if (!entry) return null;
  return entry.uid || null;
}

// Dedupe + strip falsy (mirrors alerts.resolveEmergencyTargets conventions).
const uniqTruthy = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

// Best-effort last name for a care-team label ("Dr. {lastName}'s Team").
function lastNameOf(staff, id) {
  const entry = (staff || []).find((s) => s && (s.id === id || s.uid === id));
  const name = (entry?.name || "").trim();
  if (!name) return "";
  const parts = name.split(/\s+/);
  return parts[parts.length - 1];
}

// Full display name for a staff id (used to label a per-OR chat with the person
// you're talking to, e.g. "WOR 7 · Gonzalo Martinez").
function nameOf(staff, id) {
  const entry = (staff || []).find((s) => s && (s.id === id || s.uid === id));
  return (entry?.name || "").trim();
}

// Pick the facility id off the data we already have (rooms carry facilityId;
// the roster may too). Empty string is acceptable — the day-scope + kind +
// scopeId segments are what make the id deterministic and disjoint per day.
function facilityIdOf(roster, rooms) {
  return (
    (rooms || []).find((r) => r && r.facilityId)?.facilityId ||
    roster?.facilityId ||
    ""
  );
}

// ── Channel derivation (D-04/D-05) ───────────
// Returns the channels the given `uid` may SEE (D-06 visibility): a channel is
// returned only when the uid is one of its members. Unlike alerts (board-wide),
// chat is private — so broadcast is ALSO filtered to roster members, meaning a
// non-member (e.g. a null-uid pool person, or a stranger) gets an empty list.
//
// Each channel: { id, kind, scopeId, label, memberIds:[uid], postableBy }.
//   postableBy "members" → any member may post.
//   postableBy "leaders" → only doctor/AIC/admin may post (broadcast; D-10).
export function deriveChannels(roster, rooms, staff, uid, dateKey = isoDateKey(), viewer = {}, opts = {}) {
  const facilityId = facilityIdOf(roster, rooms);
  const resolve = (id) => uidOf(staff, id);
  // Role of the signed-in viewer. We add the viewer's OWN uid to the permanent
  // channels their role entitles them to, so the channels appear (and the viewer
  // can post) EVEN IF their staff.uid self-link hasn't resolved yet — the brittle
  // A1 link no longer gates whether a logged-in doctor sees their channels. The
  // viewer is also a roster-editor, so materializing the channel writes their uid
  // into memberIds, which satisfies the channelMessages send rule.
  const isDocViewer = viewer.role === "anesthesiologist" || viewer.isAIC === true || viewer.isAdmin === true;
  // Any signed-in clinical user (doctor OR provider, incl. residents) belongs to
  // Broadcast — the org-wide channel everyone can post to (D-09 revised).
  const isAnyClinicalViewer = isDocViewer || viewer.role === "provider";
  // Resident = a provider whose staff subRole is "resident". Residents are
  // clinical staff but NOT part of "CRNA/AA Only", so a resident is dropped from
  // that channel's audience and a resident viewer is not auto-added to it.
  // Resident-ness is recorded either on staff.subRole OR staff.role depending on
  // the seeding path (real staff use subRole; the dummy team stamps role) — check
  // BOTH so a resident is excluded from "CRNA/AA Only" regardless of which field
  // carries it.
  const isResidentId = (id) => {
    const e = (staff || []).find((s) => s && (s.id === id || s.uid === id));
    return (e?.subRole || "").toLowerCase() === "resident"
      || (e?.role || "").toLowerCase() === "resident";
  };
  const viewerIsResident = (viewer.subRole || "").toLowerCase() === "resident";
  // A CRNA/AA viewer = a provider who is NOT a resident (owns "CRNA/AA Only").
  const isCrnaAaViewer = viewer.role === "provider" && !viewerIsResident;
  const withViewer = (members, include) =>
    include && uid ? uniqTruthy([...members, uid]) : members;
  const make = (kind, scopeId, label, memberIds, postableBy, permanent = false, count = null) => ({
    id: channelDocId(facilityId, dateKey, kind, scopeId),
    kind,
    scopeId,
    label,
    memberIds,
    // Display names for the people actually IN the channel, resolved once here
    // so no screen has to re-walk the staff list. Note this is the LOGGED-IN
    // audience (memberIds are auth uids), which is smaller than `count` — the
    // roster-intended audience. A member with no resolvable staff entry is
    // dropped rather than rendered as a raw uid.
    memberNames: uniqTruthy((memberIds || []).map((m) => nameOf(staff, m))),
    postableBy,
    // Permanent groups (All Providers / All Doctors / AIC) are roster-derived and
    // CANNOT be removed by users (D-04). The UI tints them and hides the remove
    // affordance; custom group rooms + DMs are removable. Per-OR/care-team are
    // derived too but not surfaced as removable rows.
    permanent,
    // Displayed member count for permanent groups = the ROSTER-intended audience
    // (set below), NOT memberIds.length. memberIds only contains people who have
    // logged in + linked their account (staff.uid), so it under-counts the real
    // roster; the count shows what the channel is FOR (all providers / all
    // doctors / the one AIC) so it matches the daily roster the AIC sees.
    count,
  });

  const crnaUids = uniqTruthy((roster?.crnas || []).map((c) => resolve(c?.id)));
  const doctorUids = uniqTruthy(
    [roster?.aicId, ...(roster?.supervisors || []).map((s) => s?.id)].map(resolve)
  );
  // Roster-intended counts (distinct people on today's list), independent of who
  // has linked their login account.
  const provCount = uniqTruthy((roster?.crnas || []).map((c) => c?.id)).length;
  const docCount = uniqTruthy([roster?.aicId, ...(roster?.supervisors || []).map((s) => s?.id)]).length;

  // "CRNA/AA Only" audience = providers who are NOT residents (doctors excluded).
  const crnaAaUids = uniqTruthy(
    (roster?.crnas || []).filter((c) => !isResidentId(c?.id)).map((c) => resolve(c?.id))
  );
  const crnaAaCount = uniqTruthy(
    (roster?.crnas || []).filter((c) => !isResidentId(c?.id)).map((c) => c?.id)
  ).length;
  // The uid of the single AIC named on the board today (D-04). Membership of the
  // AIC channel keys off THIS (roster.aicId), not the viewer's isAIC flag — so a
  // reassignment moves access to the new AIC and the old AIC loses it even if a
  // stale user.isAIC flag lingers.
  const aicUid = resolve(roster?.aicId);

  const all = [];

  // Broadcast — everyone on today's roster (D-09). ORG-WIDE: any member may post
  // AND reply (postableBy "members"), so a CRNA/AA can answer a doctor's page.
  // Visible to every signed-in clinical user (the viewer is always added). PERMANENT.
  all.push(
    make(
      CHANNEL_KINDS.broadcast,
      "all",
      "Broadcast",
      withViewer(uniqTruthy([...crnaUids, ...doctorUids]), isAnyClinicalViewer),
      "members",
      true,
      provCount + docCount
    )
  );

  // "CRNA/AA Only" — providers (CRNA/AA, residents excluded), NO doctors, so the
  // clinical-provider side has a private room. Only a CRNA/AA viewer is auto-added
  // (a doctor is never a member and cannot read it). PERMANENT. Count = CRNA/AA
  // providers on the roster (the labelled audience).
  all.push(make(CHANNEL_KINDS.allCrnas, "all", "CRNA/AA Only", withViewer(crnaAaUids, isCrnaAaViewer), "members", true, crnaAaCount));
  // "Doctors Only" — the day's doctors (incl. the AIC). A doctor viewer is always
  // a member so it shows + is postable regardless of their staff link. PERMANENT.
  all.push(make(CHANNEL_KINDS.allDoctors, "all", "Doctors Only", withViewer(doctorUids, isDocViewer), "members", true, docCount));
  // "AIC" — belongs to ONLY the current AIC of the day (+ admin for AIC↔admin /
  // announcements). Membership follows roster.aicId, so a reassignment moves it
  // to the new AIC and the old AIC drops out automatically. Count = 1. PERMANENT.
  if (aicUid || viewer.isAdmin === true) {
    all.push(make(CHANNEL_KINDS.aic, "all", "AIC", withViewer(uniqTruthy([aicUid]), viewer.isAdmin === true), "members", true, roster?.aicId ? 1 : 0));
  }

  // "All AICs" — a GLOBAL, day-scoped, cross-facility room for today's AIC of
  // EVERY active facility (+ admins). Unlike every other channel here, its
  // member set spans facilities, so it cannot be derived from the single
  // `roster` passed in — the caller (MessagingContext) must resolve each
  // active facility's board.aicId to an auth uid and pass the union in via
  // opts.crossFacilityAicUids. Omitting the opt is a no-op (existing callers
  // unaffected). The doc id uses a fixed "global" facility segment so every
  // facility's roster-editor writes the SAME doc.
  const crossAicUids = opts.crossFacilityAicUids;
  if (Array.isArray(crossAicUids)) {
    const members = withViewer(
      uniqTruthy(crossAicUids),
      viewer.isAIC === true || viewer.isAdmin === true
    );
    if (members.length > 0) {
      all.push({
        id: channelDocId("global", dateKey, CHANNEL_KINDS.aicCoordination, "all"),
        kind: CHANNEL_KINDS.aicCoordination,
        scopeId: "all",
        label: "All AICs",
        memberIds: members,
        memberNames: uniqTruthy(members.map((m) => nameOf(staff, m))),
        postableBy: "members",
        permanent: true,
        count: uniqTruthy(crossAicUids).length,
      });
    }
  }

  // Per-OR (MY ROOMS) — one 1:1 chat per room between the room's CRNA and its
  // supervising doctor. The room's supervisingDoctorId/providerId are the LIVE
  // assignment (merged in MessagingContext so this matches the board card, not a
  // stale room doc). Label names the OTHER person for the viewer ("WOR 7 ·
  // Gonzalo Martinez" for the CRNA; "WOR 7 · Casey Bennett" for the doctor), so
  // the row reads as "who am I talking to in this room". Auto-formed/removed as
  // assignments change (day-scoped ids + live re-derivation).
  for (const room of rooms || []) {
    if (!room || !room.id) continue;
    const docUid = resolve(room.supervisingDoctorId);
    const provUid = resolve(room.providerId);
    const members = uniqTruthy([docUid, provUid]);
    // A per-OR chat is "me and the other person in this room". With fewer than
    // TWO distinct people there is no other person — a solo case (the doctor
    // runs it alone), a room with a doctor but no CRNA yet, or a pair whose
    // ids resolve to the same uid. Deriving it anyway produced a room chat you
    // could only talk to yourself in, which is what surfaced on a solo case.
    // uniqTruthy already deduped, so this also covers doctor === provider.
    if (members.length < 2) continue;
    const docName = room.supervisorName || nameOf(staff, room.supervisingDoctorId);
    const provName = room.providerName || nameOf(staff, room.providerId);
    // Counterpart = whichever of the pair is NOT the viewer.
    let other;
    if (uid && provUid === uid) other = docName;       // viewer is the CRNA → show the doctor
    else if (uid && docUid === uid) other = provName;  // viewer is the doctor → show the CRNA
    else other = docName || provName;                  // materialization / non-member view
    const label = other ? `${room.name || room.id} · ${other}` : (room.name || room.id);
    all.push(make(CHANNEL_KINDS.perOr, room.id, label, members, "members"));
  }

  // Care-team — one per distinct supervising doctor; members = that doctor +
  // the CRNAs across every room they cover (deduped, null-uid dropped).
  const doctorIds = uniqTruthy((rooms || []).map((r) => r?.supervisingDoctorId));
  for (const docId of doctorIds) {
    const docUid = resolve(docId);
    if (!docUid) continue; // a null-uid doctor can't anchor a care-team
    const crnaTeam = uniqTruthy(
      (rooms || [])
        .filter((r) => r?.supervisingDoctorId === docId)
        .map((r) => resolve(r?.providerId))
    );
    const lastName = lastNameOf(staff, docId);
    all.push(
      make(
        CHANNEL_KINDS.careTeam,
        docUid,
        lastName ? `Dr. ${lastName}'s Team` : "Care Team",
        uniqTruthy([docUid, ...crnaTeam]),
        "members"
      )
    );
  }

  // opts.all → the FULL derived set, unfiltered by viewer membership. Roster-
  // editors use this to MATERIALIZE every channel doc, including ones they are not
  // a member of (e.g. a doctor writes the members-only "CRNA/AA Only" doc so the
  // providers can send). Display always uses the D-06 filter below.
  if (opts.all) return all;

  // ── ADMIN OVERSIGHT ──
  // An administrator SEES the channels they are not a member of — each room's
  // chat, care teams, other people's group rooms — marked `oversight: true`
  // and READ-ONLY. An admin is not in memberIds, so the send rule would reject
  // the write anyway; surfacing it as read-only matters because posting into a
  // room as a non-member misrepresents it to the people who believe it is
  // theirs. Watching is oversight; speaking is joining.
  //
  // ⚠️ "CRNA/AA Only" IS EXEMPT (client decision, 2026-08-04). It is the
  // providers' private space, and a private channel that management can read
  // is not private — it is a channel whose users have been misled about who is
  // in the room. Oversight of everything else is unaffected. The firestore
  // rules carry the same exemption, so this is not merely a UI omission: an
  // admin cannot reach those messages at all.
  if (viewer.isAdmin === true) {
    return all
      .filter(
        (c) => c.memberIds.includes(uid) || c.kind !== CHANNEL_KINDS.allCrnas
      )
      .map((c) => (c.memberIds.includes(uid) ? c : { ...c, oversight: true }));
  }

  // D-06 visibility: return only channels the uid is a member of. Chat is
  // private, so broadcast is membership-gated too (a non-member sees nothing).
  return all.filter((c) => c.memberIds.includes(uid));
}

// LOCAL day key, matching the roster/board day-scope (useDailyRoster
// .rosterDateKey). Inlined to keep this module React-free + dependency-free.
// Must stay LOCAL (not UTC) so derived channel ids land on the same day the
// board/roster use — a divergence would silently split channel membership.
export function isoDateKey(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// ── Send gate (D-10 — UI hint ONLY) ──────────
// "members" channels: any member may post — this now includes Broadcast (D-09
// revised: everyone can reply). The "leaders" branch is retained for any future
// leader-only channel (only an anesthesiologist / AIC / admin may post) but no
// derived channel currently uses it. This is a UI affordance hint; the
// AUTHORITATIVE boundary is the firestore rules `create` (Plan 02). Never throws.
export function canPostToChannel(channel, { role, isAIC, isAdmin } = {}) {
  if (!channel) return false;
  // Oversight = visible to an admin who is NOT a member. The server would
  // reject the send regardless (the create rule requires membership); refusing
  // here means the UI says so instead of a message bouncing off a deny rule.
  if (channel.oversight) return false;
  if (channel.postableBy === "leaders") {
    return role === "anesthesiologist" || isAIC === true || isAdmin === true;
  }
  // "members" (and any unknown postableBy) → permissive UI hint; the server
  // membership check in the rule is the real boundary.
  return true;
}

// ── Who is actually in each room today ───────
// Rooms with the live assignment merged in (the row wins, the room doc fills
// the gaps) so per-OR and care-team channels match the board exactly.
//
// ⚠️ A SOLO case is a DELIBERATE absence, not a gap. The row clears ahpId
// because the doctor runs the room alone; a plain `a.ahpId || r.providerId`
// read that intentional null as "unknown" and resurrected the room doc's stale
// providerId — the CRNA who was in the room BEFORE it went solo. The board said
// "Solo — doctor runs the case" while messaging offered a room chat with
// someone who was not there. Solo is resolved before the fallback.
// ⚠️ `nowMin` DECIDES WHICH ROW when a room has more than one. Without it this
// merged the first row Firestore returned, so a room's message channel could
// name the morning CRNA all afternoon — a chat with somebody who is not there.
// Omitted, the behaviour is exactly as before.
export function mergeRoomsWithAssignments(rooms = [], assignments = [], nowMin = null) {
  return (Array.isArray(rooms) ? rooms : []).map((r) => {
    const a = occupantRowForRoom(r?.name, assignments, nowMin) || {};
    const solo = isSoloCase(a);
    return {
      ...r,
      supervisingDoctorId: a.attendingId || r?.supervisingDoctorId || null,
      supervisorName: a.attendingName || r?.supervisorName || "",
      providerId: solo ? null : a.ahpId || r?.providerId || null,
      providerName: solo ? "" : a.ahpName || r?.providerName || "",
    };
  });
}

// ── Picker grouping (mobile) ─────────────────
// A flat list of chips stopped scaling the moment admins gained oversight of
// every channel: a busy board is ~20 rooms plus care teams plus groups, and a
// wrapping chip row would eat the screen before a single message showed. The
// phone picks a channel from a grouped list instead.
//
// Sections are ordered by how often someone reaches for them, not by the order
// deriveChannels happens to emit.
export const PICKER_SECTIONS = [
  { key: "broadcast", label: "Everyone", kinds: [CHANNEL_KINDS.broadcast] },
  {
    key: "team",
    label: "Team channels",
    kinds: [
      CHANNEL_KINDS.allDoctors,
      CHANNEL_KINDS.allCrnas,
      CHANNEL_KINDS.aic,
      CHANNEL_KINDS.aicCoordination,
    ],
  },
  { key: "rooms", label: "Rooms", kinds: [CHANNEL_KINDS.perOr, CHANNEL_KINDS.careTeam] },
  { key: "groups", label: "Group rooms", kinds: [CHANNEL_KINDS.custom] },
];

// Group channels into the picker's sections. Empty sections are dropped so the
// list never shows a heading with nothing under it. A channel of an unknown
// kind still appears (under "Other") rather than silently vanishing — a channel
// you cannot see is one you do not know exists.
export function groupChannelsForPicker(channels = []) {
  const list = Array.isArray(channels) ? channels : [];
  const claimed = new Set();
  const sections = PICKER_SECTIONS.map((section) => {
    const items = list.filter((c) => c && section.kinds.includes(c.kind));
    items.forEach((c) => claimed.add(c.id));
    return { key: section.key, label: section.label, channels: items };
  }).filter((s) => s.channels.length > 0);

  const rest = list.filter((c) => c && !claimed.has(c.id));
  if (rest.length > 0) {
    sections.push({ key: "other", label: "Other", channels: rest });
  }
  return sections;
}
